import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { ISchedule } from "@formant/core/model/ISchedule";

import { routerStore, scheduleStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Schedule extends Component {
    @localSettingObservable("settings-schedules-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-schedules-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<ISchedule>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="calendar" />,
                sortBy: _ => _.name,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions schedule={_} />,
            },
        ];

        const schedule = scheduleStore.list();
        const { filter, onChangeFilter, sort, onChangeSort } = this;
        const dimensions = [
            {
                dimensionKey: "parameterEnabled",
                dimensionName: "Parameter",
                getValue: getScheduleText,
                getValueLabel: (v: boolean) => (v ? "Enabled" : "Disabled"),
            },
        ];
        const filteredSchedule = applySimpleFilter(
            schedule || [],
            dimensions,
            getScheduleText,
            filter,
        );

        return (
            <SettingsPage
                title="Schedules"
                extra={
                    <Help
                        text="A schedule is a directive to the device to perform a specific task. You could use the built-in schedules (available by default) or create custom schedules (built using Formant APIs). On this page, you can add a friendly name, description and specify specify the device on which the schedules should appear."
                        // to="https://docs.formant.io/configure-schedules-on-web" // TODO
                    />
                }
                actions={
                    <StandardButton to={routeTo.createSchedule()}>
                        Add schedule
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="calendar"
                    loading={!schedule}
                    totalQuantity={schedule?.length || 0}
                    totalLabel="Schedules"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={schedule || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredSchedule || []}
                        columns={columns}
                        loading={schedule === undefined}
                        noResultsText="No schedules found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (schedule: ISchedule) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editSchedule(
                { id: defined(schedule.id) },
                { tab: "Settings" },
            ),
        );
    };
}

function getScheduleText(schedule: ISchedule): string {
    return [schedule.name, schedule.description].join(" ");
}
